import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { createFileRoute } from "@tanstack/react-router";

import { useBestiesWorkplaceConfig } from "@/api/bestiesWorkplaceConfig";
import { FormattedDescription } from "@/features/BestiesOnboarding/components/FormattedDescription";
import {
  WORKSPACE_CARD_PATTERNS,
  workspaceCardHoverSx,
} from "@/features/BestiesOnboarding/utils/workspaceCardUi";
import { getWorkspaceConfigData } from "@/features/BestiesOnboarding/utils/workspaceConfig";
import { getWorkspaceResourceIcon } from "@/features/BestiesOnboarding/utils/workspaceResourceIcon";
import theme from "@/theme";

export const Route = createFileRoute("/_auth/besties/$siteId/")({
  component: WorkspacePage,
});

function WorkspacePage() {
  const { data: response, isLoading, error } = useBestiesWorkplaceConfig();
  const config = getWorkspaceConfigData(response?.data);

  return (
    <>
      {isLoading ? <LinearProgress sx={{ mx: 8, mt: 5 }} /> : null}
      {error ? (
        <Alert severity="warning" sx={{ mx: 8, mt: 5 }}>
          Could not load the latest workspace resources. Showing default
          resources for now.
        </Alert>
      ) : null}

      {/* Essentials section */}
      <Box sx={{ px: 8, pb: 8, mt: 5 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5">Essentials</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            The essential resources you need to start selling Queen One.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 6 }}>
          {config.essentials.map((item, index) => (
            <Box
              key={item.title}
              sx={{
                flex: 1,
                bgcolor: "background.paper-elevation-1",
                borderRadius: `${theme.shape.borderRadiusLg}px`,
                p: 7,
                display: "flex",
                flexDirection: "column",
                gap: 5,
                ...workspaceCardHoverSx,
              }}
            >
              {/* Content */}
              <Box>
                <Typography variant="h6">{item.title}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 4 }}
                >
                  <FormattedDescription text={item.description} />
                </Typography>
              </Box>

              {/* Image area */}
              <Box
                sx={{
                  position: "relative",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: `${theme.shape.borderRadiusMd}px`,
                  height: 80,
                  overflow: "hidden",
                }}
              >
                {/* Decorative pattern — covers full area, centered */}
                <Box
                  component="img"
                  src={
                    WORKSPACE_CARD_PATTERNS[
                      index % WORKSPACE_CARD_PATTERNS.length
                    ]
                  }
                  alt=""
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    filter: "blur(8px)",
                    transform: "scale(1.15)",
                    pointerEvents: "none",
                  }}
                />
                {/* Step number */}
                <Typography
                  variant="h3"
                  sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    color: "common.white",
                  }}
                >
                  {index + 1}
                </Typography>
              </Box>

              {/* Action button */}
              <Button
                size="small"
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={getWorkspaceResourceIcon(item.cta_icon)}
                sx={{ alignSelf: "flex-start", fontSize: 14 }}
              >
                {item.cta_label}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Resources section */}
      <Box sx={{ px: 8, pb: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5">Resources</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Pitch materials and competitive positioning for brand conversations.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 5 }}>
          {config.resources.map((item) => (
            <Box
              key={item.title}
              sx={{
                flex: 1,
                bgcolor: "background.paper-elevation-1",
                borderRadius: `${theme.shape.borderRadiusLg}px`,
                p: 7,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                ...workspaceCardHoverSx,
              }}
            >
              <Typography variant="h6">{item.title}</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                <FormattedDescription text={item.description} />
              </Typography>
              <Button
                size="small"
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={getWorkspaceResourceIcon(item.cta_icon)}
                sx={{ alignSelf: "flex-start", fontSize: 14 }}
              >
                {item.cta_label}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Case Studies & Results section */}
      <Box sx={{ px: 8, pb: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5">Case Studies &amp; Results</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Walkthrough videos you can share with brands or use to train your
            team.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 6 }}>
          {config.case_studies.map((item) => (
            <Box
              key={item.title}
              sx={{
                flex: 1,
                bgcolor: "background.paper-elevation-1",
                borderRadius: `${theme.shape.borderRadiusLg}px`,
                p: 7,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                ...workspaceCardHoverSx,
              }}
            >
              <Typography variant="h6">{item.title}</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                <FormattedDescription text={item.description} />
              </Typography>
              <Button
                size="small"
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={getWorkspaceResourceIcon(item.cta_icon)}
                sx={{ alignSelf: "flex-start", fontSize: 14 }}
              >
                {item.cta_label}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Body — white content area */}
      <Box
        sx={{
          flex: 1,
          bgcolor: "common.white",
          px: 8,
        }}
      />
    </>
  );
}
